import { useMemo, useState, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

import Icons from 'assets/icons';


import { Box } from 'ui/box';

import TableComponent from 'components/partials/table-component/table-component';

import type {
  AdmitAdjustmentRow,
  NeedBucketGroup,
  UseAdmitAdjustmentsResult,
} from '../../hooks/use-admit-adjustments';

import { getAdmitAdjustmentsTableSettings } from './admit-adjustments-table-settings';

interface NeedBucketAccordionProps {
  group: NeedBucketGroup;
  ownerKey: string;
  awardType: string;
  defaultExpanded?: boolean;
  onAdmitMultiplierChange: UseAdmitAdjustmentsResult['handleAdmitMultiplierChange'];
  onYieldMultiplierChange: UseAdmitAdjustmentsResult['handleYieldMultiplierChange'];
  onRowUndo: UseAdmitAdjustmentsResult['handleRowUndo'];
  readOnly?: boolean;
}

const RANK_SUFFIX_BY_AWARD_TYPE: Record<string, string> = {
  NEED: 'Need Rank',
  AGI: 'AGI Rank',
  SAI: 'SAI Rank',
};

const NeedBucketAccordion = ({
  group,
  ownerKey,
  awardType,
  defaultExpanded = false,
  onAdmitMultiplierChange,
  onYieldMultiplierChange,
  onRowUndo,
  readOnly,
}: NeedBucketAccordionProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const handleAdmitChange = useCallback(
    (row: AdmitAdjustmentRow, value: number) => {
      onAdmitMultiplierChange(
        ownerKey,
        row.bucketRank,
        row.academicRank,
        value
      );
    },
    [onAdmitMultiplierChange, ownerKey]
  );

  const handleYieldChange = useCallback(
    (row: AdmitAdjustmentRow, value: number) => {
      onYieldMultiplierChange(
        ownerKey,
        row.bucketRank,
        row.academicRank,
        value
      );
    },
    [onYieldMultiplierChange, ownerKey]
  );

  const handleUndo = useCallback(
    (row: AdmitAdjustmentRow) => {
      onRowUndo(ownerKey, row.bucketRank, row.academicRank);
    },
    [onRowUndo, ownerKey]
  );

  const columns = useMemo(
    () =>
      getAdmitAdjustmentsTableSettings({
        onAdmitChange: handleAdmitChange,
        onYieldChange: handleYieldChange,
        onUndo: handleUndo,
      }),
    [handleAdmitChange, handleYieldChange, handleUndo]
  );

  const getRowClassName = useCallback(
    (row: AdmitAdjustmentRow) => (row.isDirty ? 'bg-navy-50' : ''),
    []
  );

  return (
    <Box
      sx={{
        borderWidth: '1px',
        borderColor: 'tokens.navy.100',
        borderRadius: '0.5rem',
        overflow: 'hidden',
      }}
    >
      <Box
        component="button"
        type="button"
        sx={[
          {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            px: 2,
            py: 1.5,
            '&:hover': { bgcolor: 'tokens.gray.50' },
            transitionProperty:
              'color, background-color, border-color, text-decoration-color, fill, stroke',
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
            transitionDuration: '150ms',
          },
          isExpanded && {
            borderBottomWidth: '1px',
            borderColor: 'tokens.navy.100',
          },
        ]}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <span className="flex items-center gap-3">
          <Icons.Arrow
            className={twMerge(
              'size-3 transition-transform text-gray-500',
              isExpanded && 'rotate-90'
            )}
            aria-hidden="true"
          />
          <span className="text-sm font-semibold text-gray-800">
            {group.label} {RANK_SUFFIX_BY_AWARD_TYPE[awardType] ?? 'Need Rank'}
          </span>
        </span>
        <span className="flex items-center gap-4 text-xs text-gray-500">
          <span>
            PY yield:{' '}
            <strong className="text-gray-700">
              {(group.pyYieldAvg * 100).toFixed(1)}%
            </strong>
          </span>
          <span>
            Proj yield:{' '}
            <strong className="text-gray-700">
              {(group.projYieldAvg * 100).toFixed(1)}%
            </strong>
          </span>
        </span>
      </Box>

      {isExpanded && (
        <div inert={readOnly || undefined} className="px-4 pb-4 pt-2">
          <TableComponent
            columns={columns}
            data={group.rows}
            isLoading={false}
            pagination={false}
            customHeight="max-h-none"
            getRowClassName={getRowClassName}
          />
        </div>
      )}
    </Box>
  );
};

export default NeedBucketAccordion;
